import type { UserProfile } from '../types';

/** V3.4 niveau-5 (5 trin) → V3.5 (7 trin). Trin 2–5 omdøbes/udvides. */
const OLD_N5_TO_NEW_COMPLETED: Record<number, number[]> = {
  1: [1],
  2: [2, 3, 4],
  3: [5],
  4: [6],
  5: [7],
};

const OLD_N5_CURRENT: Record<number, number> = {
  1: 1,
  2: 2,
  3: 5,
  4: 6,
  5: 7,
};

function parseN5TrinNum(id: string): number | null {
  const m = /^niveau-5-trin-(\d+)$/.exec(id);
  if (!m) return null;
  const n = Number(m[1]);
  return n >= 1 && n <= 5 ? n : null;
}

function highestOldN5Completed(completedTrinIds: string[]): number {
  let max = 0;
  for (const id of completedTrinIds) {
    const n = parseN5TrinNum(id);
    if (n !== null) max = Math.max(max, n);
  }
  return max;
}

function migrateCompletedN5TrinIds(completedTrinIds: string[]): string[] {
  const highest = highestOldN5Completed(completedTrinIds);
  if (highest === 0) return completedTrinIds;

  const withoutOld = completedTrinIds.filter((id) => parseN5TrinNum(id) === null);
  const next = new Set(withoutOld);

  for (let old = 1; old <= highest; old++) {
    for (const neu of OLD_N5_TO_NEW_COMPLETED[old] ?? []) {
      next.add(`niveau-5-trin-${neu}`);
    }
  }

  return [...next];
}

function migrateCurrentN5TrinId(
  currentTrinId: string,
  completedTrinIds: string[],
): string {
  const n = parseN5TrinNum(currentTrinId);
  if (n === null) return currentTrinId;

  const highest = highestOldN5Completed(completedTrinIds);
  if (highest >= n) {
    const mapped = OLD_N5_TO_NEW_COMPLETED[n];
    return mapped ? `niveau-5-trin-${mapped[mapped.length - 1]}` : currentTrinId;
  }

  return `niveau-5-trin-${OLD_N5_CURRENT[n] ?? n}`;
}

function remapN5TrinKey(id: string): string {
  const n = parseN5TrinNum(id);
  if (n === null) return id;
  const mapped = OLD_N5_CURRENT[n];
  return mapped ? `niveau-5-trin-${mapped}` : id;
}

function remapRecordKeys<T>(records: Record<string, T> | undefined): Record<string, T> {
  if (!records) return {};
  const out: Record<string, T> = {};
  for (const [key, value] of Object.entries(records)) {
    const nextKey = remapN5TrinKey(key);
    out[nextKey] = out[nextKey] ?? value;
  }
  return out;
}

/** Remap niveau-5 progress from the old 5-trin layout to 7 trin. */
export function migrateUserToV35(user: UserProfile): UserProfile {
  const hadOldN5 = [...user.completedTrinIds, user.currentTrinId].some(
    (id) => parseN5TrinNum(id) !== null,
  );
  if (!hadOldN5) return user;

  const completedTrinIds = migrateCompletedN5TrinIds(user.completedTrinIds);
  const currentTrinId = migrateCurrentN5TrinId(
    user.currentTrinId,
    user.completedTrinIds,
  );

  const miniCheckpointRecords = { ...user.miniCheckpointRecords };
  const oldMini = miniCheckpointRecords['mini-after-niveau-5-trin-3'];
  if (oldMini) {
    miniCheckpointRecords['mini-after-niveau-5-trin-5'] = oldMini;
    delete miniCheckpointRecords['mini-after-niveau-5-trin-3'];
  }

  const passedMiniCheckpointIds = (user.passedMiniCheckpointIds ?? []).map((id) =>
    id === 'mini-after-niveau-5-trin-3' ? 'mini-after-niveau-5-trin-5' : id,
  );

  return {
    ...user,
    completedTrinIds,
    currentTrinId,
    miniCheckpointRecords,
    passedMiniCheckpointIds,
    trinRecords: remapRecordKeys(user.trinRecords),
    trinProofRecords: remapRecordKeys(user.trinProofRecords),
    recentResults: user.recentResults.map((r) =>
      r.trinId ? { ...r, trinId: remapN5TrinKey(r.trinId) } : r,
    ),
  };
}
